import { useSession } from 'components/auth/useSession'
import { useLocalStorageState } from 'hooks/useStorage'
import { useEffect, useState } from 'react'

import { ConfiguracoesVideochamadaModel, DispositivoMidiaSelectModel, StorageKeys } from '../model-videochamada'

export function useConfiguracoesVideochamada(): [
  ConfiguracoesVideochamadaModel,
  (value: ConfiguracoesVideochamadaModel) => void
] {
  const { data: session } = useSession()

  const [configuracoesVideochamada, setConfiguracoesVideochamada] = useLocalStorageState<
    ConfiguracoesVideochamadaModel
  >(`${session?.profissional?.id ?? ''}/configuracoes-videochamada`)

  return [configuracoesVideochamada, setConfiguracoesVideochamada]
}

export function useStoredAudioDevice(): [
  DispositivoMidiaSelectModel,
  (audioDevice: DispositivoMidiaSelectModel) => void
] {
  const { data: session } = useSession()

  const [storedAudioDevice, setStoredAudioDevice] = useLocalStorageState<DispositivoMidiaSelectModel>(
    `${session?.profissional?.id ?? ''}/dispositivo-audio-videochamada`
  )

  return [storedAudioDevice, setStoredAudioDevice]
}

export function useStoredVideoDevice(): [
  DispositivoMidiaSelectModel,
  (videoDevice: DispositivoMidiaSelectModel) => void
] {
  const { data: session } = useSession()

  const [storedVideoDevice, setStoredVideoDevice] = useLocalStorageState<DispositivoMidiaSelectModel>(
    `${session?.profissional?.id ?? ''}/dispositivo-video-videochamada`
  )

  return [storedVideoDevice, setStoredVideoDevice]
}

export function useUserScopedStorage<T>(key: StorageKeys, initialValue: T): [T, (val: T) => void] {
  const { data: session } = useSession()
  const userId = session?.acesso?.id ?? session?.profissional?.id ?? 'guest'
  const storageKey = userId ? `${userId}/${key}` : null

  const [storedValue, setStoredValue] = useState<T>(initialValue)

  useEffect(() => {
    if (!storageKey) return

    const item = localStorage.getItem(storageKey)
    if (item) setStoredValue(JSON.parse(item))

    const onUpdate = (e: any) => {
      const updatedKey = e.key || e.detail?.key
      const newValue = e.newValue || JSON.stringify(e.detail?.newValue)

      if (updatedKey === storageKey && newValue) {
        setStoredValue(JSON.parse(newValue))
      }
    }

    globalThis.addEventListener('storage', onUpdate)
    globalThis.addEventListener('local-storage-update' as any, onUpdate)
    return () => {
      globalThis.removeEventListener('storage', onUpdate)
      globalThis.removeEventListener('local-storage-update' as any, onUpdate)
    }
  }, [storageKey])

  const setValue = (value: T) => {
    if (!storageKey) return
    localStorage.setItem(storageKey, JSON.stringify(value))
    setStoredValue(value)
    globalThis.dispatchEvent(new CustomEvent('local-storage-update', { detail: { key: storageKey, newValue: value } }))
  }

  return [storedValue, setValue]
}

export function useStoredAudioEnabled(initialValue = true): [boolean, (enabled: boolean) => void] {
  return useUserScopedStorage(StorageKeys.AUDIO_ENABLED, initialValue)
}

export function useStoredVideoEnabled(initialValue = true): [boolean, (enabled: boolean) => void] {
  return useUserScopedStorage(StorageKeys.VIDEO_ENABLED, initialValue)
}
